/* ============================================================
   EDITORIAL STORYTELLER — logique
   ------------------------------------------------------------
   - Multi-instances sur la même page (chaque .es-section indépendante)
   - preload="none" → metadata uniquement quand le bloc devient suivant
   - IntersectionObserver : auto-advance pause hors viewport
   - Page Visibility API : pause si onglet inactif
   - prefers-reduced-motion respecté côté CSS
   - requestAnimationFrame pour la progress bar
   - Re-init automatique dans l'éditeur Shopify
   ============================================================ */

(function () {
  'use strict';

  class EditorialStoryteller {
    constructor(el) {
      this.el = el;
      this.advanceMode = el.dataset.advanceMode || 'auto';
      this.blockDuration = (parseInt(el.dataset.blockDuration, 10) || 5) * 1000;
      this.count = parseInt(el.dataset.blockCount, 10) || 0;

      if (this.count < 2) {
        this.initSingle();
        return;
      }

      this.medias = el.querySelectorAll('.es-media');
      this.slides = el.querySelectorAll('.es-slide');
      this.navItems = el.querySelectorAll('.es-nav__item');
      this.navButtons = el.querySelectorAll('[data-go-to]');
      this.progressFill = el.querySelector('.es-nav__progress-fill');

      this.current = 0;
      this.isVisible = false;
      this.isPaused = false;
      this.rafId = null;
      this.startTime = 0;
      this.elapsed = 0;
      this._endedHandler = null;

      this.mobileQuery = window.matchMedia('(max-width: 749px)');

      this.bindEvents();
      this.observeVisibility();
      this.preloadNext(0);
      this.activateMedia(0, true);
    }

    initSingle() {
      const media = this.el.querySelector('.es-media');
      if (media) this.playMediaIn(media);
    }

    bindEvents() {
      this.navButtons.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          const idx = parseInt(e.currentTarget.dataset.goTo, 10);
          this.goTo(idx, true);
        });
      });

      this._onVisibility = () => {
        if (document.hidden) this.pause();
        else if (this.isVisible) this.resume();
      };
      document.addEventListener('visibilitychange', this._onVisibility);
    }

    observeVisibility() {
      if (!('IntersectionObserver' in window)) {
        this.isVisible = true;
        this.scheduleNext();
        return;
      }
      this._io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.isVisible = entry.intersectionRatio > 0.4;
            if (this.isVisible) this.resume();
            else this.pause();
          });
        },
        { threshold: [0, 0.4, 0.8] }
      );
      this._io.observe(this.el);
    }

    getActiveMediaElement(mediaWrapper) {
      const isMobile = this.mobileQuery.matches;
      const selector = isMobile
        ? '.es-media__video--mobile, .es-media__image--mobile'
        : '.es-media__video--desktop, .es-media__image--desktop';
      return mediaWrapper.querySelector(selector);
    }

    playMediaIn(mediaWrapper) {
      const el = this.getActiveMediaElement(mediaWrapper);
      if (!el) return null;
      if (el.tagName === 'VIDEO') {
        try {
          el.currentTime = 0;
        } catch (_) {}
        if (el.preload === 'none') el.load();
        const p = el.play();
        if (p && p.catch) p.catch(() => {});
        return el;
      }
      return el;
    }

    pauseMediaIn(mediaWrapper) {
      mediaWrapper.querySelectorAll('video').forEach((v) => {
        if (!v.paused) v.pause();
      });
    }

    preloadNext(currentIdx) {
      const nextIdx = (currentIdx + 1) % this.count;
      const nextMedia = this.medias[nextIdx];
      if (!nextMedia) return;
      const nextEl = this.getActiveMediaElement(nextMedia);
      if (nextEl && nextEl.tagName === 'VIDEO' && nextEl.preload === 'none') {
        nextEl.preload = 'metadata';
      }
    }

    activateMedia(idx, isInitial) {
      this.medias.forEach((m, i) => {
        if (i === idx) {
          m.classList.add('is-active');
          this.playMediaIn(m);
        } else {
          m.classList.remove('is-active');
          // Pause les autres après la transition pour éviter freeze visuel
          setTimeout(() => this.pauseMediaIn(m), isInitial ? 0 : 700);
        }
      });

      this.slides.forEach((s, i) => s.classList.toggle('is-active', i === idx));
      this.navItems.forEach((n, i) => n.classList.toggle('is-active', i === idx));

      // Position de la ligne globale au début du bloc courant
      if (this.progressFill) {
        this.progressFill.style.width = (idx / this.count) * 100 + '%';
      }
    }

    goTo(idx, userTriggered) {
      if (idx === this.current) return;
      this.cancelTick();
      this.current = idx;
      this.activateMedia(idx);
      this.preloadNext(idx);
      if (userTriggered) this.elapsed = 0;
      this.scheduleNext();
    }

    scheduleNext() {
      if (!this.isVisible || this.isPaused) return;
      this.cancelTick();

      const activeMedia = this.medias[this.current];
      const activeEl = this.getActiveMediaElement(activeMedia);
      const isVideo = activeEl && activeEl.tagName === 'VIDEO';

      if (this.advanceMode === 'video' && isVideo) {
        const onEnded = () => {
          activeEl.removeEventListener('ended', onEnded);
          this.next();
        };
        activeEl.addEventListener('ended', onEnded);
        this._endedHandler = { el: activeEl, fn: onEnded };
        this.tickProgressFromVideo(activeEl);
      } else {
        this.startTime = performance.now() - this.elapsed;
        this.tick();
      }
    }

    tick() {
      const loop = () => {
        const now = performance.now();
        this.elapsed = now - this.startTime;
        const ratio = Math.min(this.elapsed / this.blockDuration, 1);
        if (this.progressFill) {
          const globalRatio = (this.current + ratio) / this.count;
          this.progressFill.style.width = globalRatio * 100 + '%';
        }
        if (ratio >= 1) {
          this.elapsed = 0;
          this.next();
          return;
        }
        this.rafId = requestAnimationFrame(loop);
      };
      this.rafId = requestAnimationFrame(loop);
    }

    tickProgressFromVideo(videoEl) {
      const loop = () => {
        if (!videoEl.duration || isNaN(videoEl.duration)) {
          this.rafId = requestAnimationFrame(loop);
          return;
        }
        const ratio = Math.min(videoEl.currentTime / videoEl.duration, 1);
        if (this.progressFill) {
          const globalRatio = (this.current + ratio) / this.count;
          this.progressFill.style.width = globalRatio * 100 + '%';
        }
        if (ratio < 1) this.rafId = requestAnimationFrame(loop);
      };
      this.rafId = requestAnimationFrame(loop);
    }

    cancelTick() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      if (this._endedHandler) {
        this._endedHandler.el.removeEventListener('ended', this._endedHandler.fn);
        this._endedHandler = null;
      }
    }

    next() {
      const nextIdx = (this.current + 1) % this.count;
      this.current = nextIdx;
      this.activateMedia(nextIdx);
      this.preloadNext(nextIdx);
      this.elapsed = 0;
      this.scheduleNext();
    }

    pause() {
      this.isPaused = true;
      this.cancelTick();
      const activeMedia = this.medias[this.current];
      if (activeMedia) this.pauseMediaIn(activeMedia);
    }

    resume() {
      this.isPaused = false;
      const activeMedia = this.medias[this.current];
      if (activeMedia) this.playMediaIn(activeMedia);
      this.scheduleNext();
    }

    destroy() {
      this.cancelTick();
      if (this._io) this._io.disconnect();
      if (this._onVisibility) {
        document.removeEventListener('visibilitychange', this._onVisibility);
      }
      this.el._editorialStoryteller = null;
    }
  }

  // ===== Init multi-instances =====
  function initAll(scope) {
    const root = scope || document;
    root.querySelectorAll('.es-section').forEach((el) => {
      if (!el._editorialStoryteller) {
        el._editorialStoryteller = new EditorialStoryteller(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // ===== Shopify theme editor =====
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target && e.target.querySelector) initAll(e.target);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const sec = e.target && e.target.querySelector('.es-section');
    if (sec && sec._editorialStoryteller) sec._editorialStoryteller.destroy();
  });

  document.addEventListener('shopify:section:select', (e) => {
    if (e.target && e.target.querySelector) initAll(e.target);
  });
})();